import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import SiteHeader from "../SiteHeader";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";
import {
    FiCheckCircle,
    FiLink,
    FiUsers,
    FiMessageSquare,
    FiArrowRight,
    FiSmile,
} from "react-icons/fi";
import "../../assets/Dashboard.css";
import "../../App.css";

const Dashboard = () => {
    const { t } = useTranslation();
    const [isLoading, setIsLoading] = useState(true);
    const [topGroupChats, setTopGroupChats] = useState([]);
    const [topUrls, setTopUrls] = useState([]);
    const [totalUrlCount, setTotalUrlCount] = useState(0);
    const [totalGroupChatCount, setTotalGroupChatCount] = useState(0);
    const [error, setError] = useState("");
    const baseURL = process.env.REACT_APP_BASE_URL;

    const location = useLocation();
    const navigate = useNavigate();
    // incompleteGroupCount (from ChatSelectionsPage.js, sourced from
    // /api/save-all-data's incomplete_group_count) - how many donated groups
    // could not be fully synced even after the automatic rescan retry. This
    // is a known, occasional consequence of WhatsApp's own server-side sync
    // behavior (see docs/planning/BAILEYS_EVALUATION.md §3.1), not an error -
    // shown here as a gentle note, not a warning.
    const { selection, incompleteGroupCount } = location.state || {};

    useEffect(() => {
        if (selection === "yes") {
            const fetchData = async () => {
                try {
                    const [groupChatsRes, urlsRes, urlCountRes, groupChatCountRes] =
                        await Promise.all([
                            axios.get(new URL("/api/fetch-dashboard-group-chats", baseURL)),
                            axios.get(new URL("/api/fetch-dashboard-donor-urls", baseURL)),
                            axios.get(new URL("/api/count-unique-donated-urls", baseURL)),
                            axios.get(new URL("/api/count-unique-group-chats", baseURL)),
                        ]);

                    setTopGroupChats(groupChatsRes.data);
                    setTopUrls(urlsRes.data);
                    setTotalUrlCount(urlCountRes.data);
                    setTotalGroupChatCount(groupChatCountRes.data);
                } catch (err) {
                    setError(err);
                    console.error("Dashboard error fetching data:", err);
                } finally {
                    setIsLoading(false);
                }
            };

            fetchData();
        } else {
            setIsLoading(false);
        }
    }, [baseURL, selection]);

    if (isLoading) {
        return (
            <div className="dash-app">
                <div className="dash-loading">
                    <div className="dash-loading-spinner" />
                    <p>{t("dashboard.preparing")}</p>
                </div>
            </div>
        );
    }

    if (error) {
        console.warn("ERROR CAUGHT: ", error);
        return (
            <div className="dash-app">
                <div className="dash-loading">
                    <p>{t("dashboard.wentWrong", { message: error.message || t("dashboard.unknownError") })}</p>
                </div>
            </div>
        );
    }

    return (
        <div className="dash-app">
            <SiteHeader />

            {selection === "yes" ? (
                <>
                    <section className="dash-hero">
                        <div className="dash-hero-badge">
                            <FiCheckCircle />
                        </div>
                        <h1>{t("dashboard.thanks")}</h1>
                        <p>
                            {t("dashboard.quickLook")}
                        </p>
                        {Number(incompleteGroupCount) > 0 && (
                            <div className="dash-hero-note">
                                {t("dashboard.incompleteNote", { groups: incompleteGroupCount === 1 ? t("dashboard.oneGroup") : t("dashboard.nGroups", { count: incompleteGroupCount }) })}
                            </div>
                        )}
                    </section>

                    <div className="dash-body">
                        <div className="dash-stat-grid">
                            <div className="dash-stat-card">
                                <div className="dash-stat-icon">
                                    <FiUsers />
                                </div>
                                <div>
                                    <div className="dash-stat-value">
                                        {totalGroupChatCount}
                                    </div>
                                    <div className="dash-stat-label">
                                        {t("dashboard.groupsDonated")}
                                    </div>
                                </div>
                            </div>

                            <div className="dash-stat-card">
                                <div className="dash-stat-icon cyan">
                                    <FiLink />
                                </div>
                                <div>
                                    <div className="dash-stat-value">
                                        {totalUrlCount}
                                    </div>
                                    <div className="dash-stat-label">
                                        {t("dashboard.uniqueLinks")}
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="dash-section-label">{t("dashboard.topGroups")}</div>
                        <div className="dash-panel">
                            <div className="dash-panel-header">
                                <FiMessageSquare />
                                {t("dashboard.topByMessages", { n: Math.min(topGroupChats.length, 10) })}
                            </div>
                            {topGroupChats.length > 0 ? (
                                <ul className="dash-list">
                                    {topGroupChats.slice(0, 10).map((chat, idx) => (
                                        <li className="dash-list-item" key={chat.subject}>
                                            <span className="dash-rank">{idx + 1}</span>
                                            <span className="dash-item-name">
                                                {chat.subject}
                                            </span>
                                            <span className="dash-item-count">
                                                {chat.donated_message_nbr}
                                            </span>
                                        </li>
                                    ))}
                                </ul>
                            ) : (
                                <p className="dash-empty-row">
                                    {t("dashboard.noGroupActivity")}
                                </p>
                            )}
                        </div>

                        <div className="dash-section-label">{t("dashboard.linksShared")}</div>
                        <div className="dash-panel">
                            <div className="dash-panel-header">
                                <FiLink />
                                {t("dashboard.topUrls", { n: Math.min(topUrls.length, 10) })}
                            </div>
                            {topUrls.length > 0 ? (
                                <ul className="dash-list">
                                    {topUrls.slice(0, 10).map((urlObj, idx) => (
                                        <li className="dash-list-item" key={urlObj.url}>
                                            <span className="dash-rank">{idx + 1}</span>
                                            <a
                                                className="dash-item-name"
                                                href={urlObj.url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                title={urlObj.url}
                                            >
                                                {urlObj.url}
                                            </a>
                                        </li>
                                    ))}
                                </ul>
                            ) : (
                                <p className="dash-empty-row">
                                    {t("dashboard.noLinks")}
                                </p>
                            )}
                        </div>

                        <div className="dash-finish-row">
                            <button
                                className="dash-finish-btn"
                                onClick={() => navigate("/thank-you", { replace: true })}
                            >
                                {t("dashboard.finish")}
                                <FiArrowRight />
                            </button>
                        </div>
                    </div>
                </>
            ) : (
                <div className="dash-declined">
                    <div className="dash-declined-icon">
                        <FiSmile />
                    </div>
                    <h2>
                        {t("dashboard.declined")}
                    </h2>
                    <button
                        className="dash-exit-btn"
                        onClick={() => window.close()}
                    >
                        {t("common.close")}
                    </button>
                </div>
            )}

            <footer className="dash-footer">
                <div className="dash-footer-logo">GU</div>
                <p>{t("common.footer", { year: new Date().getFullYear() })}</p>
            </footer>
        </div>
    );
};

export default Dashboard;
